import { UsersRound } from 'lucide-react';
import { useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Avatar } from '../../components/ui/Avatar.tsx';
import { Drawer } from '../../components/ui/Drawer.tsx';
import { computePosition } from '../../components/ui/position.ts';
import { useNow } from '../../components/ui/RelativeTime.tsx';
import { cx } from '../../lib/cx.ts';
import { MEDIA, useMediaQuery, useOnClickOutside } from '../../lib/hooks.ts';
import { presenceStatusText, usePresenceList, type PresenceItem } from './api.ts';
import './presence.css';

const STACK = 3;

/** Topbar: stacked avatars of staff online now; opens a list (popover on desktop, bottom sheet on phones). */
export function PresenceMenu() {
  const { data } = usePresenceList();
  const isPhone = useMediaQuery(MEDIA.phone);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const close = (restoreFocus: boolean) => {
    setOpen(false);
    setPos(null);
    if (restoreFocus) triggerRef.current?.focus();
  };

  useOnClickOutside([panelRef, triggerRef], () => close(false), open && !isPhone);

  useLayoutEffect(() => {
    if (!open || isPhone) return;
    const place = () => {
      const t = triggerRef.current;
      const p = panelRef.current;
      if (!t || !p) return;
      setPos(computePosition(t.getBoundingClientRect(), { width: p.offsetWidth, height: p.offsetHeight }, 'bottom', 'end', 6));
    };
    place();
    panelRef.current?.focus({ preventScroll: true });
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, isPhone]);

  if (!data) return null;
  const online = data.items.filter((i) => i.online);
  const label = `ทีมงานออนไลน์ ${online.length} คน`;
  const description = `ออนไลน์ ${online.length} จาก ${data.items.length} คน`;

  const onPanelKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close(true);
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="presence-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={() => (open ? close(false) : setOpen(true))}
      >
        {online.length > 0 ? (
          <span className="presence-stack" aria-hidden="true">
            {online.slice(0, STACK).map((i) => (
              <span key={i.id} className="presence-stack__item">
                <Avatar name={i.displayName} size={28} />
              </span>
            ))}
          </span>
        ) : (
          <UsersRound size={18} strokeWidth={1.75} aria-hidden="true" />
        )}
        <span className="presence-trigger__count num" aria-hidden="true">
          <span className="presence-dot is-online" />
          {online.length}
          <span className="presence-trigger__text">ออนไลน์</span>
        </span>
      </button>

      {isPhone ? (
        <Drawer open={open} onClose={() => close(true)} title="ทีมงานออนไลน์" description={description} width={380}>
          <PresenceRows items={data.items} />
        </Drawer>
      ) : (
        open &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label="ทีมงานออนไลน์"
            tabIndex={-1}
            className="presence-panel"
            style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}
            onKeyDown={onPanelKeyDown}
          >
            <header className="presence-panel__header">
              <p className="presence-panel__title">ทีมงานออนไลน์</p>
              <p className="presence-panel__desc">{description}</p>
            </header>
            <PresenceRows items={data.items} />
          </div>,
          document.body,
        )
      )}
    </>
  );
}

function PresenceRows({ items }: { items: PresenceItem[] }) {
  const now = new Date(useNow());
  if (items.length === 0) return <p className="presence-empty">ยังไม่มีทีมงานในระบบ</p>;
  return (
    <ul className="presence-list">
      {items.map((i) => (
        <li key={i.id} className="presence-row" data-testid="presence-row" data-online={i.online || undefined}>
          <span className="presence-avatar">
            <Avatar name={i.displayName} size={36} />
            <span className={cx('presence-dot', 'presence-avatar__dot', i.online && 'is-online')} aria-hidden="true" />
          </span>
          <span className="presence-row__main">
            <span className="presence-row__name">
              <span className="truncate">{i.displayName}</span>
              {i.isSelf && <span className="presence-row__self">(คุณ)</span>}
            </span>
            <span className="presence-row__meta">
              <span className="presence-row__role">{i.roleLabelTh}</span>
              <span aria-hidden="true"> · </span>
              <span className={cx('presence-row__status', i.online && 'is-online')}>{presenceStatusText(i, now)}</span>
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}
